import type { FilterMatrixRow, FilterState } from '../types';

// Cross-dimension faceting from the matrix endpoint. For each of the four
// filter dimensions, returns a map of value → count under the *other*
// dimensions' selections (self-exclusion — otherwise picking a value would
// collapse its own dropdown to just that value). Consumers walk their base
// tree and look up each node's value to decide enabled/disabled + count.
//
// Matrix coverage is incomplete on purpose: it does not include `query`
// (full-text), `prefix`, or `titleId`. Those filters apply to the entries
// table but not the projection in /api/filter-matrix, so derived facets
// ignore them. This is acceptable — the result is a strict superset of the
// actually-available data, never a subset.

type Predicate = (row: FilterMatrixRow) => boolean;

function buildMatrixPredicate(
  filters: FilterState,
  exclude: Set<keyof FilterState>,
): Predicate {
  const preds: Predicate[] = [];

  if (!exclude.has('einzelplan') && filters.einzelplan) {
    const ep = filters.einzelplan;
    preds.push(r => r.einzelplan === ep);
  }

  // kapitel filter format: 'ep|kap' or 'ep|kap|year' (multi-era). The matrix
  // has no year/kapitel_text, so we ignore the era segment.
  if (!exclude.has('kapitel') && filters.kapitel) {
    const [kapEp, kapKap] = filters.kapitel.split('|');
    preds.push(r => r.einzelplan === kapEp && r.kapitel === kapKap);
  }

  if (!exclude.has('expenditureType') && filters.expenditureType) {
    const v = filters.expenditureType;
    preds.push(r => r.expenditure_type === v);
  }

  // Mirrors the three-branch subExpType logic in buildWhereClause exactly.
  if (!exclude.has('subExpType') && filters.subExpType) {
    const sub = filters.subExpType;
    if (sub.includes('|')) {
      const [parentType, subType] = sub.split('|');
      if (subType === parentType) {
        preds.push(r => r.expenditure_type === parentType);
      } else if (subType === 'Nicht zugeordnet' || subType === '') {
        preds.push(r =>
          r.expenditure_type === parentType &&
          (r.sub_expenditure_type == null ||
            r.sub_expenditure_type === '' ||
            r.sub_expenditure_type === r.expenditure_type),
        );
      } else {
        preds.push(r =>
          r.expenditure_type === parentType && r.sub_expenditure_type === subType,
        );
      }
    } else {
      preds.push(r => r.sub_expenditure_type === sub);
    }
  }

  // Funktion cascade: deepest-set slot wins (matches buildWhereClause).
  const funktionExcluded =
    exclude.has('funktion') && exclude.has('oberfunktion') && exclude.has('hauptfunktion');
  if (!funktionExcluded) {
    if (!exclude.has('funktion') && filters.funktion) {
      const v = filters.funktion;
      preds.push(r => r.funktion === v);
    } else if (!exclude.has('oberfunktion') && filters.oberfunktion) {
      const v = filters.oberfunktion;
      preds.push(r => r.oberfunktion === v);
    } else if (!exclude.has('hauptfunktion') && filters.hauptfunktion) {
      const v = filters.hauptfunktion;
      preds.push(r => r.hauptfunktion === v);
    }
  }

  // Gruppierung cascade.
  const gruppierungExcluded =
    exclude.has('gruppierung') && exclude.has('obergruppe') && exclude.has('hauptgruppe');
  if (!gruppierungExcluded) {
    if (!exclude.has('gruppierung') && filters.gruppierung) {
      const v = filters.gruppierung;
      preds.push(r => r.gruppierung === v);
    } else if (!exclude.has('obergruppe') && filters.obergruppe) {
      const v = filters.obergruppe;
      preds.push(r => r.obergruppe === v);
    } else if (!exclude.has('hauptgruppe') && filters.hauptgruppe) {
      const hg = filters.hauptgruppe;
      if (hg.includes(',')) {
        const set = new Set(hg.split(','));
        preds.push(r => r.hauptgruppe != null && set.has(r.hauptgruppe));
      } else {
        preds.push(r => r.hauptgruppe === hg);
      }
    }
  }

  if (preds.length === 0) return () => true;
  return r => preds.every(p => p(r));
}

function aggregate(
  matrix: FilterMatrixRow[],
  filters: FilterState,
  exclude: Set<keyof FilterState>,
  emit: (row: FilterMatrixRow, add: (key: string, count: number) => void) => void,
): Map<string, number> {
  const pred = buildMatrixPredicate(filters, exclude);
  const counts = new Map<string, number>();
  const add = (key: string, count: number) => {
    counts.set(key, (counts.get(key) ?? 0) + count);
  };
  for (const row of matrix) {
    if (!pred(row)) continue;
    emit(row, add);
  }
  return counts;
}

const POLITIKFELD_SLOTS = new Set<keyof FilterState>(['expenditureType', 'subExpType']);
const HAUSHALTSSTELLE_SLOTS = new Set<keyof FilterState>(['einzelplan', 'kapitel']);
const GRUPPIERUNG_SLOTS = new Set<keyof FilterState>([
  'hauptgruppe',
  'obergruppe',
  'gruppierung',
]);
const FUNKTION_SLOTS = new Set<keyof FilterState>([
  'hauptfunktion',
  'oberfunktion',
  'funktion',
]);

export interface FilterAvailability {
  // Map from politikfeld tree node value (expType or 'expType|subType') to
  // count under all non-politikfeld filters. Mirrors buildPolitikfeldTree:
  // children only emit when subType is distinct, non-empty, non-self.
  politikfeld: Map<string, number>;
  // Keys: einzelplan code, plus 'ep|kap' (no era — the multi-era 'ep|kap|year'
  // base node is matched by stripping the year segment in the consumer).
  haushaltsstelle: Map<string, number>;
  // Codes are unique within the dimension across all three levels, so the
  // node value alone is a sufficient lookup key.
  gruppierung: Map<string, number>;
  funktion: Map<string, number>;
}

export function deriveFilterAvailability(
  matrix: FilterMatrixRow[],
  filters: FilterState,
): FilterAvailability {
  return {
    politikfeld: aggregate(matrix, filters, POLITIKFELD_SLOTS, (row, add) => {
      if (!row.expenditure_type) return;
      add(row.expenditure_type, row.count);
      const sub = row.sub_expenditure_type;
      if (sub != null && sub !== '' && sub !== row.expenditure_type) {
        add(`${row.expenditure_type}|${sub}`, row.count);
      }
    }),
    haushaltsstelle: aggregate(matrix, filters, HAUSHALTSSTELLE_SLOTS, (row, add) => {
      if (!row.einzelplan) return;
      add(row.einzelplan, row.count);
      if (row.kapitel != null && row.kapitel !== '') {
        add(`${row.einzelplan}|${row.kapitel}`, row.count);
      }
    }),
    gruppierung: aggregate(matrix, filters, GRUPPIERUNG_SLOTS, (row, add) => {
      if (row.hauptgruppe) add(row.hauptgruppe, row.count);
      if (row.obergruppe) add(row.obergruppe, row.count);
      if (row.gruppierung) add(row.gruppierung, row.count);
    }),
    funktion: aggregate(matrix, filters, FUNKTION_SLOTS, (row, add) => {
      if (row.hauptfunktion) add(row.hauptfunktion, row.count);
      if (row.oberfunktion) add(row.oberfunktion, row.count);
      if (row.funktion) add(row.funktion, row.count);
    }),
  };
}

// Strip the optional era segment from a haushaltsstelle base-tree node value
// so it matches the matrix-derived 'ep|kap' availability key. Multi-era
// chapters use 'ep|kap|year' for stable IDs across renames; the matrix has
// no era info, so all variants share the same availability.
export function haushaltsstelleAvailabilityKey(nodeValue: string): string {
  const parts = nodeValue.split('|');
  return parts.length >= 2 ? `${parts[0]}|${parts[1]}` : nodeValue;
}
